/**
 * Справочник городов и районов для автокомплита.
 *
 * Районы — административное деление верхнего уровня (для Москвы это округа, а не 125
 * районов). Аналитика режет данные по бакету `city × district × vehicle × company × timeSlot`
 * и прячет бакет, пока в нём меньше `bk.analytics.confidence-threshold-min-submissions`
 * отчётов, — мелкая нарезка означала бы, что порог не наберётся никогда.
 *
 * ponytail: статичный список, а не справочник ФИАС/ГАР. Города добавляются руками по мере
 * выхода в новые регионы; если однажды понадобится вся Россия — брать готовый датасет,
 * а не дописывать этот файл.
 */
export const CITY_DISTRICTS: Record<string, string[]> = {
  'Москва': [
    'Центральный', 'Северный', 'Северо-Восточный', 'Восточный', 'Юго-Восточный',
    'Южный', 'Юго-Западный', 'Западный', 'Северо-Западный', 'Зеленоградский',
    'Новомосковский', 'Троицкий',
  ],
  'Санкт-Петербург': [
    'Адмиралтейский', 'Василеостровский', 'Выборгский', 'Калининский', 'Кировский',
    'Колпинский', 'Красногвардейский', 'Красносельский', 'Кронштадтский', 'Курортный',
    'Московский', 'Невский', 'Петроградский', 'Петродворцовый', 'Приморский',
    'Пушкинский', 'Фрунзенский', 'Центральный',
  ],
  'Новосибирск': [
    'Дзержинский', 'Железнодорожный', 'Заельцовский', 'Калининский', 'Кировский',
    'Ленинский', 'Октябрьский', 'Первомайский', 'Советский', 'Центральный',
  ],
  'Екатеринбург': [
    'Верх-Исетский', 'Железнодорожный', 'Кировский', 'Ленинский', 'Октябрьский',
    'Орджоникидзевский', 'Чкаловский',
  ],
  'Казань': [
    'Авиастроительный', 'Вахитовский', 'Кировский', 'Московский', 'Ново-Савиновский',
    'Приволжский', 'Советский',
  ],
  'Нижний Новгород': [
    'Автозаводский', 'Канавинский', 'Ленинский', 'Московский', 'Нижегородский',
    'Приокский', 'Советский', 'Сормовский',
  ],
  'Челябинск': [
    'Калининский', 'Курчатовский', 'Ленинский', 'Металлургический', 'Советский',
    'Тракторозаводский', 'Центральный',
  ],
  'Красноярск': [
    'Железнодорожный', 'Кировский', 'Ленинский', 'Октябрьский', 'Свердловский',
    'Советский', 'Центральный',
  ],
  'Самара': [
    'Железнодорожный', 'Кировский', 'Красноглинский', 'Куйбышевский', 'Ленинский',
    'Октябрьский', 'Промышленный', 'Самарский', 'Советский',
  ],
  'Уфа': [
    'Дёмский', 'Калининский', 'Кировский', 'Ленинский', 'Октябрьский',
    'Орджоникидзевский', 'Советский',
  ],
  'Ростов-на-Дону': [
    'Ворошиловский', 'Железнодорожный', 'Кировский', 'Ленинский', 'Октябрьский',
    'Первомайский', 'Пролетарский', 'Советский',
  ],
  'Омск': ['Кировский', 'Ленинский', 'Октябрьский', 'Советский', 'Центральный'],
  'Краснодар': ['Западный', 'Карасунский', 'Прикубанский', 'Центральный'],
  'Воронеж': [
    'Железнодорожный', 'Коминтерновский', 'Левобережный', 'Ленинский', 'Советский',
    'Центральный',
  ],
  'Пермь': [
    'Дзержинский', 'Индустриальный', 'Кировский', 'Ленинский', 'Мотовилихинский',
    'Орджоникидзевский', 'Свердловский',
  ],
};

export const CITIES = Object.keys(CITY_DISTRICTS);

/**
 * Приводит написанное пользователем к каноничному варианту из справочника: регистр, ё/е и
 * дефисы вместо пробелов ("ростов на дону" → "Ростов-на-Дону"). Незнакомое значение
 * возвращается как есть — справочник неполон, и запрещать ввод нельзя.
 */
export function canonicalCity(input: string): string {
  return canonical(input, CITIES);
}

export function canonicalDistrict(city: string, input: string): string {
  return canonical(input, CITY_DISTRICTS[canonicalCity(city)] ?? []);
}

function canonical(input: string, options: string[]): string {
  const trimmed = input.trim();
  if (!trimmed) return '';
  const key = normalize(trimmed);
  return options.find((o) => normalize(o) === key) ?? trimmed;
}

function normalize(s: string): string {
  return s.toLowerCase().replace(/ё/g, 'е').replace(/[\s-]+/g, '-');
}

/**
 * Приблизительный центр каждого города — только чтобы сопоставить координаты с городом из
 * справочника. Для района этого мало: тут нет ни границ, ни центроидов районов, и подменять
 * их «ближайшим центром» значило бы уверенно ставить неверный район.
 */
export const CITY_CENTRES: Record<string, [number, number]> = {
  'Москва': [55.7558, 37.6173],
  'Санкт-Петербург': [59.9391, 30.3159],
  'Новосибирск': [55.0084, 82.9357],
  'Екатеринбург': [56.8389, 60.6057],
  'Казань': [55.7963, 49.1088],
  'Нижний Новгород': [56.3269, 44.0059],
  'Челябинск': [55.1644, 61.4368],
  'Красноярск': [56.0153, 92.8932],
  'Самара': [53.1959, 50.1002],
  'Уфа': [54.7351, 55.9587],
  'Ростов-на-Дону': [47.2225, 39.7189],
  'Омск': [54.9885, 73.3242],
  'Краснодар': [45.0355, 38.9753],
  'Воронеж': [51.6720, 39.1843],
  'Пермь': [58.0105, 56.2502],
};

/** Дальше этого от центра — считаем, что курьер не в известном нам городе. */
const MAX_CITY_RADIUS_KM = 60;

/**
 * Город по координатам, или `undefined` если ближайший известный город слишком далеко —
 * молча подставить чужой город хуже, чем не подставить ничего.
 */
export function cityFromCoords(lat: number, lon: number): string | undefined {
  let best: string | undefined;
  let bestKm = Infinity;
  for (const [city, [clat, clon]] of Object.entries(CITY_CENTRES)) {
    const km = haversineKm(lat, lon, clat, clon);
    if (km < bestKm) { bestKm = km; best = city; }
  }
  return bestKm <= MAX_CITY_RADIUS_KM ? best : undefined;
}

function haversineKm(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371;
  const rad = (d: number) => (d * Math.PI) / 180;
  const dLat = rad(lat2 - lat1);
  const dLon = rad(lon2 - lon1);
  const a = Math.sin(dLat / 2) ** 2 +
    Math.cos(rad(lat1)) * Math.cos(rad(lat2)) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(a)));
}
